import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { TenantInvitationsApi } from "../../../api/common/apiIndex";
import Loader from "../../common/commonUtils/Loader";

const statusBadgeClass = (status) => {
  const s = String(status || "").toUpperCase();
  if (s === "ACCEPTED") return "badge bg-success-subtle text-success-emphasis border";
  if (s === "PENDING") return "badge bg-primary-subtle text-primary-emphasis border";
  if (s === "EXPIRED") return "badge bg-secondary-subtle text-secondary-emphasis border";
  if (s === "REVOKED") return "badge bg-danger-subtle text-danger-emphasis border";
  return "badge bg-body-tertiary text-body border";
};

const formatDateTime = (iso) => {
  if (!iso) return "No expiry";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

export default function InvitationsPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [size] = useState(15);
  const [sort] = useState("createdAt,desc");

  const {
    data,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["tenant-invitations", "active", { page, size, sort }],
    queryFn: () => TenantInvitationsApi.listActive({ page, size, sort }),
    staleTime: 60_000,
  });

  const normalized = useMemo(() => {
    if (!data) return { content: [], totalPages: 0, totalElements: 0 };
    if (Array.isArray(data)) return { content: data, totalPages: 1, totalElements: data.length };
    const content = Array.isArray(data.content) ? data.content : [];
    const totalPages = Number.isFinite(data.totalPages) ? data.totalPages : 0;
    const totalElements = Number.isFinite(data.totalElements) ? data.totalElements : content.length;
    return { content, totalPages, totalElements };
  }, [data]);

  const invitations = normalized.content;
  const totalPages = normalized.totalPages;
  const totalElements = normalized.totalElements;
  const hasInvites = invitations.length > 0;

  const rangeText = useMemo(() => {
    if (!totalElements) return "0–0 of 0";
    const start = page * size + 1;
    const end = Math.min(start + invitations.length - 1, totalElements);
    return `${start}–${end} of ${totalElements}`;
  }, [page, size, invitations.length, totalElements]);

  return (
    <div className="d-flex flex-column gap-3">
      <div className="card border shadow-sm rounded-4">
        <div className="card-body d-flex flex-wrap align-items-center justify-content-between gap-2">
          <div>
            <h5 className="mb-1">Invitations</h5>
            <p className="mb-0 text-body-secondary">Track pending invites sent to teammates.</p>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary rounded-pill"
              onClick={() => navigate("/home/create-user?mode=invite")}
            >
              <i className="bi bi-envelope-plus me-2" aria-hidden="true" />
              Invite an agent
            </button>
          </div>
        </div>
      </div>

      <div className="card border shadow-sm rounded-4">
        <div className="card-body ">
          {isError && (
            <div className="alert alert-danger m-3 mb-0" role="alert">
              {error?.response?.data?.message || error?.message || "Failed to load invitations."}
            </div>
          )}

          {isLoading && !data ? (
            <div className="py-5">
              <Loader text="Loading invitations…" />
            </div>
          ) : (
            <div className="table-responsive">
              {hasInvites ? (
                <table className="table align-middle mb-0">
                  <thead className="bg-body-tertiary text-body-secondary">
                    <tr>
                      <th scope="col">Email</th>
                      <th scope="col">Roles</th>
                      <th scope="col">Created</th>
                      <th scope="col">Expires</th>
                      <th scope="col">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invitations.map((inv) => (
                      <tr key={inv.id}>
                        <td className="fw-semibold">{inv.email || "—"}</td>
                        <td>
                          <div className="d-flex flex-wrap gap-1">
                            {(inv.roles || []).map((r, idx) => (
                              <span key={`${r}-${idx}`} className="badge bg-body-secondary text-body fw-normal">
                                {r}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td>{formatDateTime(inv.createdAt)}</td>
                        <td>{inv.expiresAt ? formatDateTime(inv.expiresAt) : "No expiry"}</td>
                        <td>
                          <span className={statusBadgeClass(inv.status)}>{String(inv.status || "").toUpperCase() || "—"}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="p-4 text-center">
                  <p className="mb-2 text-body-secondary">No active invitations yet.</p>
                  <button
                    type="button"
                    className="btn btn-primary rounded-pill"
                    onClick={() => navigate("/home/create-user?mode=invite")}
                  >
                    Invite an agent
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="card-footer d-flex flex-wrap align-items-center justify-content-between gap-2">
          <small className="text-body-secondary">{rangeText}{isLoading ? " …" : ""}</small>
          <div className="d-flex align-items-center gap-2">
            <button
              className="btn btn-outline-secondary btn-sm rounded-pill"
              disabled={page === 0 || isLoading}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              Prev
            </button>
            <span className="text-body-secondary small">
              Page {totalPages === 0 ? 0 : page + 1} / {totalPages || 0}
            </span>
            <button
              className="btn btn-outline-secondary btn-sm rounded-pill"
              disabled={page + 1 >= totalPages || isLoading}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
